import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dismissToast, getToasts, subscribeToasts } from './toastStore';
import { t } from '../../i18n/i18n';
import './Toast.scss';

const EXIT_MS = 180;
const PEEK = 14; // how much of each toast behind the front one stays visible
const SCALE_STEP = 0.05;
const GAP = 8; // spacing once the stack is hovered open

/** Outline glyphs, drawn inline so they take the tone colour from `currentColor`. */
const GLYPH = {
  success: <path d="m8.2 12 2.6 2.6 5-5.6" />,
  error: (
    <>
      <path d="M9 9l6 6" />
      <path d="M15 9l-6 6" />
    </>
  ),
  warning: (
    <>
      <path d="M12 7.6v5" />
      <path d="M12 16.1h.01" />
    </>
  ),
  info: (
    <>
      <path d="M12 16.4v-5" />
      <path d="M12 7.9h.01" />
    </>
  ),
};

function ToastIcon({ tone }) {
  return (
    <svg className="toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      {GLYPH[tone] ?? GLYPH.info}
    </svg>
  );
}

/** `index` counts back from the front of the deck: 0 is the newest, on top. */
function ToastItem({ item, index, count, offset, expanded, slotRef }) {
  const [leaving, setLeaving] = useState(false);

  const close = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => dismissToast(item.id), EXIT_MS);
  };

  // Hovering the stack holds every toast open so the ones behind can be read.
  useEffect(() => {
    if (!item.duration || expanded) return undefined;
    const timer = setTimeout(close, item.duration);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [item.seq, item.duration, expanded]);

  return (
    <div
      ref={slotRef}
      className="toast-slot"
      style={{
        transform: expanded
          ? `translateY(${offset}px)`
          : `translateY(${offset}px) scale(${1 - index * SCALE_STEP})`,
        opacity: expanded ? 1 : Math.max(1 - index * 0.28, 0.35),
        zIndex: count - index,
      }}
    >
      <div
        className={`toast toast--${item.tone}${leaving ? ' is-leaving' : ''}`}
        role={item.tone === 'error' ? 'alert' : 'status'}
        onClick={close}
      >
        <ToastIcon tone={item.tone} />
        <div className="toast__text">
          <p className="toast__title">{t(item.title)}</p>
          {item.description && <p className="toast__desc">{t(item.description)}</p>}
        </div>
      </div>
    </div>
  );
}

/** Mounted once, in App.js — `toast.*` from any page renders through here. */
export default function ToastViewport() {
  const [items, setItems] = useState(getToasts);
  const [expanded, setExpanded] = useState(false);
  const [heights, setHeights] = useState([]);
  const slots = useRef(new Map());

  useEffect(() => subscribeToasts(setItems), []);

  // Newest first: the front of the deck is the toast that just fired.
  const stack = [...items].reverse();

  // Re-measured on hover and on resize too: a toast that reflows to another
  // line changes height, and the expanded offsets are stacked from these.
  useLayoutEffect(() => {
    const measure = () =>
      setHeights(stack.map((item) => slots.current.get(item.id)?.offsetHeight ?? 0));
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line
  }, [items, expanded]);

  useEffect(() => {
    if (!items.length) setExpanded(false);
  }, [items.length]);

  if (typeof document === 'undefined') return null;

  const offsetOf = (index) =>
    expanded
      ? heights.slice(0, index).reduce((total, h) => total + h + GAP, 0)
      : index * PEEK;

  const height = expanded
    ? heights.reduce((total, h) => total + h + GAP, 0) - GAP
    : (heights[0] ?? 0) + PEEK * Math.max(stack.length - 1, 0);

  return createPortal(
    <div
      className={`toast-viewport${expanded ? ' is-expanded' : ''}`}
      style={{ height: Math.max(height, 0) }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      aria-live="polite"
    >
      {stack.map((item, index) => (
        <ToastItem
          key={item.id}
          item={item}
          index={index}
          count={stack.length}
          offset={offsetOf(index)}
          expanded={expanded}
          slotRef={(node) => {
            if (node) slots.current.set(item.id, node);
            else slots.current.delete(item.id);
          }}
        />
      ))}
    </div>,
    document.body
  );
}
